import axiosInstance from './AxiosInstance';

const RolesService = {
  // 1. CREATE ROLE
  createRole: async (roleData) => {
    try {
      const response = await axiosInstance.post('/roles', roleData);
      return {
        success: response.data.success,
        data: response.data.data.role,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create role',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 2. GET ALL ROLES
  getAllRoles: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add pagination
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Add filters
      if (params.search) queryParams.append('search', params.search);
      if (params.status) queryParams.append('status', params.status);

      const response = await axiosInstance.get(`/roles?${queryParams.toString()}`);
      return {
        success: response.data.success,
        data: response.data.data.data,
        pagination: response.data.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch roles',
        data: [],
        pagination: {}
      };
    }
  },

  // 3. GET ROLE BY ID
  getRoleById: async (id) => {
    try {
      const response = await axiosInstance.get(`/roles/${id}`);
      return {
        success: response.data.success,
        data: response.data.data.role,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch role',
        data: null
      };
    }
  },

  // 4. UPDATE ROLE
  updateRole: async (id, roleData) => {
    try {
      const response = await axiosInstance.put(`/roles/${id}`, roleData);
      return {
        success: response.data.success,
        data: response.data.data.role,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update role',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 5. DELETE ROLE
  deleteRole: async (id) => {
    try {
      const response = await axiosInstance.delete(`/roles/${id}`);
      return {
        success: response.data.success,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete role'
      };
    }
  },

  // 6. ASSIGN ROLE TO USER
  assignRoleToUser: async (assignmentData) => {
    try {
      const response = await axiosInstance.post('/roles/assign', assignmentData);
      return {
        success: response.data.success,
        data: response.data.data.assignment,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to assign role',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 7. GET USER ASSIGNMENTS
  getUserAssignments: async (userId) => {
    try {
      const response = await axiosInstance.get(`/roles/assignments/${userId}`);
      return {
        success: response.data.success,
        data: response.data.data.assignments,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch user assignments',
        data: []
      };
    }
  },

  // 8. UPDATE ROLE ASSIGNMENT
  updateRoleAssignment: async (id, assignmentData) => {
    try {
      const response = await axiosInstance.put(`/roles/assignments/${id}`, assignmentData);
      return {
        success: response.data.success,
        data: response.data.data.assignment,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update role assignment',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 9. DELETE ROLE ASSIGNMENT
  deleteRoleAssignment: async (id) => {
    try {
      const response = await axiosInstance.delete(`/roles/assignments/${id}`);
      return {
        success: response.data.success,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete role assignment'
      };
    }
  },

  // 10. GET ROLES DROPDOWN
  getRoleDropdown: async () => {
    try {
      const response = await axiosInstance.get('/roles/dropdown');
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch roles dropdown',
        data: []
      };
    }
  },

  // Legacy method names for backward compatibility
  getRoles: async (params = {}) => {
    return await RolesService.getAllRoles(params);
  },

  getRolesForSelect: async () => {
    return await RolesService.getAllRoles({ limit: 100 });
  },

  // Helper method for search
  searchRoles: async (searchTerm) => {
    return await RolesService.getAllRoles({ search: searchTerm, limit: 50 });
  },

  // Helper method for status filtering
  getRolesByStatus: async (status) => {
    return await RolesService.getAllRoles({ status });
  },

  // Helper method for getting active roles
  getActiveRoles: async () => {
    return await RolesService.getAllRoles({ status: 'Active' });
  }
};

export default RolesService;
